import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@/components/Icon";
import { Screen } from "@/components/Layout";
import { Spinner } from "@/components/Controls";
import EmberOrb from "@/components/EmberOrb";
import { colors, spacing, radius, fonts, type } from "@/theme";
import { logError } from "@/lib/log";
import { getStoredUserId, fetchUser, fetchCheckin } from "@/api";

function formatDate(iso) {
  if (!iso) return "recently";
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "earlier today";
  if (days === 1) return "yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
}

export default function WelcomeBack() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [memories, setMemories] = useState([]);
  const [events, setEvents] = useState([]);
  const [lastDate, setLastDate] = useState(null);
  const [opener, setOpener] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const id = getStoredUserId();
      if (!id) {
        navigate("/onboarding", { replace: true });
        return;
      }
      try {
        const data = await fetchUser(id);
        setName(data.user.first_name);
        setMemories(data.memories);
        setEvents(data.events ?? []);
        setLastDate(data.user.last_conversation_at ?? null);
        fetchCheckin(id).then(setOpener);
      } catch (e) { logError("WelcomeBack load", e); }
      setLoading(false);
    })();
  }, [navigate]);

  const cont = () => navigate("/conversation", opener ? { replace: true, state: { opener } } : { replace: true });

  if (loading) {
    return <Screen><div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}><Spinner size={40} /></div></Screen>;
  }

  return (
    <Screen testID="welcome-back-screen" style={{ display: "flex", flexDirection: "column", padding: `${spacing.xl}px ${spacing.xl}px 0` }}>
      <div style={{ flex: 1, overflowY: "auto", paddingTop: spacing.lg }}>
        <div style={{ marginBottom: spacing.xl }}><EmberOrb size={96} /></div>
        <h1 data-testid="welcome-back-heading" style={{ ...fonts.displayBold, fontSize: type["3xl"], lineHeight: 1.15, color: colors.onSurface, margin: 0 }}>
          Welcome back,<br />{name}
        </h1>
        <p style={{ ...fonts.textSemiBold, fontSize: type.lg, color: colors.muted, marginTop: spacing.md }}>We last talked {formatDate(lastDate)}.</p>

        {opener && (
          <button data-testid="checkin-card" onClick={cont} className="ember-press"
            style={{ display: "flex", alignItems: "center", gap: spacing.md, width: "100%", textAlign: "left", background: colors.brandTertiary, borderRadius: radius.lg, padding: spacing.lg, marginTop: spacing.xl }}>
            <div style={{ width: 44, height: 44, borderRadius: 22, background: colors.brandPrimary, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name="heart" size={22} color={colors.onBrandPrimary} />
            </div>
            <span style={{ flex: 1, ...fonts.textSemiBold, fontSize: type.base, lineHeight: 1.45, color: colors.onBrandTertiary }}>{opener}</span>
          </button>
        )}

        {(memories.length > 0 || events.length > 0) && (
          <div style={{ marginTop: spacing["2xl"] }}>
            <div style={{ ...fonts.textBold, fontSize: type.lg, color: colors.onSurface, marginBottom: spacing.lg }}>Here&rsquo;s what I remember</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: spacing.md }}>
              {events.map((e) => (
                <div key={e.id} data-testid={`event-chip-${e.id}`} style={{ ...chip, background: colors.brandPrimary }}>
                  <Icon name="calendar" size={18} color={colors.onBrandPrimary} />
                  <span style={{ ...fonts.textBold, fontSize: type.base, color: colors.onBrandPrimary }}>{e.event}{e.time ? ` · ${e.time}` : ""}</span>
                </div>
              ))}
              {memories.slice(0, 8).map((m) => (
                <div key={m.id} data-testid={`memory-chip-${m.id}`} style={chip}>
                  <Icon name="bookmark" size={18} color={colors.brand} />
                  <span style={{ ...fonts.textBold, fontSize: type.base, color: colors.onBrandTertiary }}>{m.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ position: "sticky", bottom: 0, padding: `${spacing.md}px 0 ${spacing.xl}px`, background: colors.surface }}>
        <button data-testid="continue-talking-button" onClick={cont} className="ember-press"
          style={{ width: "100%", minHeight: 76, borderRadius: radius.lg, background: colors.brandPrimary, display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.md }}>
          <Icon name="mic" size={26} color={colors.onBrandPrimary} />
          <span style={{ ...fonts.textExtraBold, fontSize: type.xl, color: colors.onBrandPrimary }}>Continue talking</span>
        </button>
      </div>
    </Screen>
  );
}

const chip = { display: "flex", alignItems: "center", gap: spacing.sm, background: colors.brandTertiary, borderRadius: radius.pill, padding: `${spacing.md}px ${spacing.lg}px`, minHeight: 56 };
